import { plural } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { type ImportFinding, takeoverActionSignature } from '@magig/schemas';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { CampaignAnatomyDrawer } from '../components/campaign-anatomy-drawer.js';
import { Mag } from '../components/mag.js';
import { NewAdGroupModal, type NewAdGroupSeed } from '../components/new-ad-group-modal.js';
import { NewRsaModal } from '../components/new-rsa-modal.js';
import type { RsaDescriptionSeed, RsaHeadlineSeed } from '../components/rsa-form-fields.js';
import { RsaSwapModal } from '../components/rsa-swap-modal.js';
import { magToast } from '../components/toaster.js';
import { TopNav } from '../components/top-nav.js';
import {
  type CampaignMetrics,
  type CampaignSnapshot,
  deleteImportedCampaign,
  dismissTakeoverRecommendation,
  fetchAutopilotHeartbeat,
  fetchCampaignAutopilot,
  type ImportedCampaignDetail,
  isAutoApplyEligible,
  listTakeoverDismissals,
  listTakeoverExecutions,
  moveImportedCampaignToBrand,
  refreshImportedCampaign,
  restoreTakeoverDismissal,
  setGoogleAdGroupStatus,
  setGoogleAdStatus,
  setGoogleCampaignStatus,
  setImportedCampaignBudget,
  type TakeoverActionContextPayload,
  type TakeoverActionExecution,
  type TakeoverActionItem,
  type TakeoverExecutionItem,
  type TakeoverImpactPanel,
  type TakeoverRead,
  updateGoogleBudget,
} from '../lib/api.js';
import { useBrandTitle } from '../lib/document-title.js';
import { founderErrorCopy } from '../lib/error-copy.js';
import { formatNumber, formatRelativeTimeCompact } from '../lib/format.js';
import { importedCampaignQuery, meQuery } from '../lib/queries.js';
import { AutopilotToggle } from './brand.$slug.campaigns.$id.js';

// /campaigns/imported/$id — the takeover review screen. This page
// is intentionally NOT a Google-Ads replica: the founder already has
// Google Ads for the raw view. What lands here is the budget
// conversation card + Mag's "what i'd do" actions with their per-
// action rationale. The earlier two-column snapshot+mag layout
// (ad-group rows, ad rows, per-resource pause/swap buttons, 30-day
// metrics) was retired — that kind of admin tooling belongs on the
// platform's own UI.

export const Route = createFileRoute('/brand/$slug/campaigns/imported/$id')({
  beforeLoad: async ({ params, context: { queryClient } }) => {
    const me = await queryClient.ensureQueryData(meQuery());
    if (!me) throw redirect({ to: '/login' });
    const campaign = await queryClient.ensureQueryData(importedCampaignQuery(params.id));
    if (!campaign) throw redirect({ to: '/brand/$slug/campaigns', params: { slug: params.slug } });
    return { me, campaign };
  },
  loader: ({ context }) => context as { me: unknown; campaign: ImportedCampaignDetail },
  component: ImportedCampaignPage,
});

function ImportedCampaignPage() {
  const { campaign } = Route.useLoaderData();
  const { slug } = Route.useParams();
  const { snapshot, magRead } = campaign;
  // Named before the message sees them: an expression inside <Trans>
  // extracts as {0}, and a translator reordering an unnamed placeholder
  // is guessing at what it holds.
  const accountLabel = campaign.adAccountLabel;
  const platform = snapshot.platform;
  const adType = snapshot.type;
  const importedAgo = formatRelative(campaign.importedAt);
  useBrandTitle(slug, [snapshot.name]);

  return (
    <main className="min-h-screen bg-[var(--cream)]">
      <TopNav active="campaigns" />
      <section className="mx-auto max-w-[1120px] px-4 pb-16 pt-8 sm:px-7">
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
          <div>
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
              {'↘'} <Trans>campaigns · taken over</Trans>
            </p>
            <h1 className="mt-2 font-display text-[26px] sm:text-[30px] font-black leading-[1.05] tracking-[-0.04em]">
              {snapshot.name}
            </h1>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--ink)]/55">
              <Trans>
                {accountLabel} · {platform} · {adType} · imported {importedAgo}
              </Trans>
            </p>
          </div>
          <Link to="/brand/$slug/campaigns" params={{ slug }} className="link-quiet text-[12px]">
            {'↘'} <Trans>back to campaigns</Trans>
          </Link>
        </div>

        {/* s207 — both columns can target the same write surfaces
            (status toggles, budget, RSA swap), so the page hoists a
            shared snapshot state + the swap-modal target ad. The
            snapshot column hands actions to local helpers; the
            mag-read column passes through ActionItem's "approve"
            buttons. */}
        <ImportedCampaignBody
          campaignId={campaign.id}
          initialSnapshot={snapshot}
          brandId={campaign.brandId}
          provider={campaign.provider}
          magRead={magRead}
          slug={slug}
          initialDailyBudgetCents={campaign.dailyBudgetUsdCents}
          sharedWith={campaign.sharedWith}
        />
      </section>
    </main>
  );
}

/** Page-level target for the new-rsa modal. `seed` is null for
 *  founder-initiated opens; populated when mag's action-item approve
 *  pre-fills the form. */
interface NewRsaTarget {
  adGroupResourceId: string;
  seed: {
    headlines: RsaHeadlineSeed[];
    descriptions: RsaDescriptionSeed[];
    finalUrl: string;
  } | null;
  /** Archival context — present when the modal was opened from an
   *  ActionItem approve, null when it was a founder-initiated "+ add
   *  rsa" click. The modal threads it through to the api so an
   *  execution row gets recorded for the "how's it doing?" panel. */
  actionContext: TakeoverActionContextPayload | null;
}

/** Page-level target for the new-ad-group modal. */
interface NewAdGroupTarget {
  seed: NewAdGroupSeed | null;
  actionContext: TakeoverActionContextPayload | null;
}

/** [12.2L-110] Swap modal target. `draft` is null for founder-
 *  initiated opens (the modal pre-fills with the ad's existing copy);
 *  populated when mag's action-item approve passes through her draft
 *  rewrite, in which case the modal opens pre-filled with that. */
interface SwapTarget {
  adId: string;
  draft: {
    headlines: RsaHeadlineSeed[];
    descriptions: RsaDescriptionSeed[];
    finalUrl: string;
  } | null;
  actionContext: TakeoverActionContextPayload | null;
}

function ImportedCampaignBody({
  campaignId,
  initialSnapshot,
  brandId,
  provider,
  magRead,
  slug,
  initialDailyBudgetCents,
  sharedWith,
}: {
  campaignId: string;
  initialSnapshot: CampaignSnapshot;
  brandId: string;
  provider: 'google' | 'meta' | 'tiktok';
  magRead: TakeoverRead | null;
  slug: string;
  initialDailyBudgetCents: number | null;
  sharedWith: Array<{ id: string; name: string; slug: string }>;
}) {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const [snapshot, setSnapshot] = useState(initialSnapshot);
  // Mag's read is loader-fed but lifted to state so the post-write
  // refresh can replace it (without it, the prop wouldn't change
  // until the next route navigation + loader run).
  const [magReadState, setMagReadState] = useState<TakeoverRead | null>(magRead);

  // [12.2L-95] When autopilot is on, the founder doesn't need the
  // 30-day numbers staring at them — that's the whole point of
  // handing over the wheel. Surface mag's read + planned actions
  // full-width and tuck the snapshot under a disclosure they can
  // pop open when they actually want to look. Same cache key the
  // AutopilotToggle uses, so this is a shared hit not a second fetch.
  const autopilotQ = useQuery({
    queryKey: ['campaign', campaignId, 'autopilot'],
    queryFn: () => fetchCampaignAutopilot(campaignId),
    staleTime: 30_000,
  });
  const autopilotOn = autopilotQ.data?.on === true;

  // Post-write refresh — fires in the background after each
  // successful Google write. Always pulls a fresh snapshot (so the
  // anatomy drawer + per-action "shipped" detection catch up). Only
  // re-runs mag's read agent when autopilot is ON; when autopilot
  // is OFF the founder gets a stable recommendation list across
  // their batch of approvals (chasing a moving target felt awful).
  // The manual `↻ refresh mag's read` button below the action list
  // is the explicit way to get a new read when in manual mode.
  //
  // Also busts the autopilot eligibility cache — direct google
  // writes append `approved` verdicts to the trust ledger (strikes
  // counter), so the founder should see the 0/3 → 1/3 → 2/3
  // progression without having to reload.
  const refreshAfterWrite = useCallback(async () => {
    try {
      const fresh = await refreshImportedCampaign(campaignId, { skipMagRead: !autopilotOn });
      setSnapshot(fresh.snapshot);
      setMagReadState(fresh.magRead);
    } catch (err) {
      console.warn('[imported] post-write refresh failed', err);
    }
    void queryClient.invalidateQueries({ queryKey: ['campaign', campaignId, 'autopilot'] });
    // Bust the executions cache too so the new "how's it doing?"
    // panel appears under the freshly-shipped action without a
    // reload.
    void queryClient.invalidateQueries({
      queryKey: ['imported-campaign', campaignId, 'executions'],
    });
  }, [campaignId, queryClient, autopilotOn]);

  // Explicit "give me a fresh read" button — used when autopilot is
  // OFF, since `refreshAfterWrite` no longer re-runs the agent in
  // that mode. Hits the same endpoint with skipMagRead=false so the
  // takeover-read + budget-recommender both run.
  const [magReadRefreshing, setMagReadRefreshing] = useState(false);
  const refreshMagReadManually = useCallback(async () => {
    if (magReadRefreshing) return;
    setMagReadRefreshing(true);
    try {
      const fresh = await refreshImportedCampaign(campaignId, { skipMagRead: false });
      setSnapshot(fresh.snapshot);
      setMagReadState(fresh.magRead);
      magToast.success(t`read refreshed.`, t`here's what i see now.`);
    } catch (err) {
      magToast.error("couldn't refresh.", founderErrorCopy(err, 'try again in a sec.'));
    } finally {
      setMagReadRefreshing(false);
    }
  }, [campaignId, magReadRefreshing, t]);

  // [12.2L-110] Swap target now carries an optional draft so action
  // approvals can pre-fill the modal with mag's draft headlines /
  // descriptions / url instead of the existing-copy fallback. null
  // adId = no modal open.
  const [swapTarget, setSwapTarget] = useState<SwapTarget | null>(null);
  /** s208 + s209 — modal targets for the "+ add new" affordances.
   *  Lifted to the page level so both columns can open them.
   *  s209: the target now carries an optional `seed` for the
   *  mag-draft pre-fill path. `seed === null` opens the modal
   *  blank (founder-initiated); a populated seed opens it pre-
   *  filled with mag's draft (action-item approve). */
  const [newRsaTarget, setNewRsaTarget] = useState<NewRsaTarget | null>(null);
  const [newAdGroupTarget, setNewAdGroupTarget] = useState<NewAdGroupTarget | null>(null);
  /** Campaign-anatomy drawer — read-only "what's in this campaign"
   *  panel. Hidden by default; opens from a small link above the
   *  KPI band so a founder who wants to verify what mag's referring
   *  to can drill in without leaving the page. */
  const [anatomyOpen, setAnatomyOpen] = useState(false);
  const writesAvailable = provider === 'google';

  return (
    <>
      {/* Unification roadmap Phase 5 (UI surface only) — the autopilot
          toggle now renders on imported campaigns too. The api endpoint
          /api/campaigns/:id/autopilot accepts both origins after Phase
          1's schema unification; the autopilot SCHEDULER acting on
          imported rows is the deferred half of Phase 5 (founder can
          flip the toggle here but no autonomous actions fire yet —
          the scheduler extension lands in a focused session per the
          roadmap). */}
      <KpiBand snapshot={snapshot} />
      <div className="mt-2 flex justify-end">
        <button
          type="button"
          onClick={() => setAnatomyOpen(true)}
          className="font-mono text-[10.5px] uppercase tracking-[0.12em] text-[var(--plum)] transition-colors duration-150 hover:text-[var(--ink)]"
        >
          {'↘'} <Trans>what's in this campaign</Trans> {'→'}
        </button>
      </div>
      <div className="mt-4">
        <AutopilotToggle campaign={{ id: campaignId }} />
      </div>
      <BudgetConversationCard
        campaignId={campaignId}
        recommendation={magReadState?.budgetRecommendation ?? null}
        initialDailyBudgetCents={initialDailyBudgetCents}
        autopilotOn={autopilotOn}
      />
      {/* The takeover view used to be a two-column layout — Google-
          Ads-shaped snapshot on the left (campaign metrics + ad
          group rows + ad rows + per-resource pause / swap buttons)
          and Mag's read on the right. Pulled the snapshot column
          and the "what's working / what's not" diagnostic blocks:
          if the founder wants the raw Google view they open
          Google Ads. This surface is Mag's recommendations + their
          explanations, period. */}
      <div className="mt-5">
        <MagReadColumn
          campaignId={campaignId}
          read={magReadState}
          campaignName={snapshot.name}
          slug={slug}
          brandId={brandId}
          snapshot={snapshot}
          setSnapshot={setSnapshot}
          setMagReadState={setMagReadState}
          writesAvailable={writesAvailable}
          autopilotOn={autopilotOn}
          onOpenSwap={(target) => setSwapTarget(target)}
          onOpenNewRsaWithSeed={(target) => setNewRsaTarget(target)}
          onOpenNewAdGroupWithSeed={(target) => setNewAdGroupTarget(target)}
          onAfterWrite={refreshAfterWrite}
          onRefreshMagRead={refreshMagReadManually}
          magReadRefreshing={magReadRefreshing}
        />
      </div>

      {/* Founder-direct pause/resume on google — independent of
          mag's recommendations. Sits above remove-from-magig
          because pausing is a softer action (no data destroyed)
          and the founder reaches for it more often. */}
      <PauseOnGoogleSection
        campaignId={campaignId}
        brandId={brandId}
        snapshot={snapshot}
        setSnapshot={setSnapshot}
      />

      {/* s206 — RSA swap modal lives at the page level so action
          items in the mag-read column can open it too. */}
      {/* [spec move-campaign-between-sharing-brands] Only when the account is
          actually shared — empty sharedWith is the single-brand case, which is
          almost every account, and it renders nothing at all. Placed ABOVE the
          delete section: moving is the gentler answer to "this is in the wrong
          place", and a founder who finds remove first may delete and re-import
          instead, losing mag's read. */}
      {sharedWith.length > 0 ? (
        <MoveCampaignBrandSection campaignId={campaignId} sharedWith={sharedWith} />
      ) : null}

      <DeleteImportedCampaignSection campaignId={campaignId} slug={slug} name={snapshot.name} />

      <RsaSwapTrigger
        snapshot={snapshot}
        target={swapTarget}
        brandId={brandId}
        onClose={() => setSwapTarget(null)}
        onSwapped={(oldAdId, newAdProviderId, newHeadlines, newDescriptions) => {
          setSnapshot((s) => {
            const swappedOut = s.ads.map((ad) =>
              ad.providerAdId === oldAdId ? { ...ad, status: 'removed' as const } : ad,
            );
            const newAd = {
              providerAdId: newAdProviderId,
              type: 'responsive search ad',
              name: 'rsa · just swapped',
              status: 'enabled' as const,
              headlines: newHeadlines,
              descriptions: newDescriptions,
              adStrength: null,
              metrics: emptyMetrics(),
            };
            return { ...s, ads: [...swappedOut, newAd] };
          });
          // Background refresh: pull a fresh snapshot + re-run
          // takeover-read so mag's recommendation list catches up.
          void refreshAfterWrite();
        }}
      />

      {/* s208 + s209 — new ad group modal. campaignResourceId is the
          current snapshot's id; the optional seed comes from mag's
          takeover-read draft when this open was triggered via an
          action-item approve. On success we optimistically push a
          new ad group row so the founder sees it immediately. */}
      <NewAdGroupModal
        open={newAdGroupTarget !== null}
        brandId={brandId}
        campaignResourceId={snapshot.providerCampaignId}
        campaignLabel={snapshot.name}
        seed={newAdGroupTarget?.seed ?? null}
        actionContext={newAdGroupTarget?.actionContext ?? null}
        onCancel={() => setNewAdGroupTarget(null)}
        onCreated={(result, submitted) => {
          // The createAdGroup chain returns resource names in
          // [budget?, adGroup, ad?, keywords...] order. We pick out
          // the ad_group + first ad_group_ad resource names.
          const newAdGroupResourceId =
            extractIdFromResourceName(result.resourceNames, /\/adGroups\/(\d+)$/) ??
            `pending-${Date.now()}`;
          const newAdResourceId = submitted.initialAd
            ? extractIdFromResourceName(result.resourceNames, /\/adGroupAds\/(\d+)$/)
            : null;
          const mockBadge = result.via === 'mock' ? ' (mock)' : '';
          magToast.success(
            `ad group shipped${mockBadge}.`,
            submitted.initialAd
              ? 'live on google ads — first metrics arrive on the next scan.'
              : 'live on google ads — add ads to start serving.',
          );
          setSnapshot((s) => {
            const newAdGroup = {
              providerAdGroupId: newAdGroupResourceId,
              name: submitted.name,
              status: 'enabled' as const,
              biddingSummary: `manual cpc · max $${submitted.cpcBidUsd.toFixed(2)}`,
              keywordCount: submitted.keywordCount,
              metrics: emptyMetrics(),
            };
            const newAds =
              submitted.initialAd && newAdResourceId
                ? [
                    ...s.ads,
                    {
                      providerAdId: newAdResourceId,
                      type: 'responsive search ad',
                      name: 'rsa · just shipped',
                      status: 'enabled' as const,
                      headlines: submitted.initialAd.headlines,
                      descriptions: submitted.initialAd.descriptions,
                      adStrength: null,
                      metrics: emptyMetrics(),
                    },
                  ]
                : s.ads;
            return { ...s, adGroups: [...s.adGroups, newAdGroup], ads: newAds };
          });
          setNewAdGroupTarget(null);
          void refreshAfterWrite();
        }}
      />

      {/* s208 + s209 — new RSA modal. The target ad-group id is set
          when the founder clicks "+ add rsa" on a specific row OR
          when mag's takeover-read approve triggers a create_rsa
          action with a pre-filled draft. */}
      <NewRsaTrigger
        snapshot={snapshot}
        target={newRsaTarget}
        brandId={brandId}
        onClose={() => setNewRsaTarget(null)}
        onCreated={(newAdProviderId, headlines, descriptions) => {
          setSnapshot((s) => ({
            ...s,
            ads: [
              ...s.ads,
              {
                providerAdId: newAdProviderId,
                type: 'responsive search ad',
                name: 'rsa · just added',
                status: 'enabled' as const,
                headlines,
                descriptions,
                adStrength: null,
                metrics: emptyMetrics(),
              },
            ],
          }));
          void refreshAfterWrite();
        }}
      />

      <CampaignAnatomyDrawer
        open={anatomyOpen}
        onClose={() => setAnatomyOpen(false)}
        snapshot={snapshot}
      />
    </>
  );
}

// ─── Phase 4 — budget conversation card ──────────────────────────────
// Renders above the snapshot/magread grid when mag produced a budget
// recommendation. Three primary actions: accept the recommendation,
// keep the current (snapshot-derived) spend, or pick a different
// number. Founder's pick persists via /api/imported-campaigns/:id/budget;
// the campaign row gets `daily_budget_usd_cents` set, which the
// autopilot scheduler will consume in Phase 5.
//
// Renders nothing when the recommendation is missing — legacy imports
// (pre-Phase-4) skip the surface cleanly. Once a budget is persisted
// (locked-in state), the card collapses into a compact receipt with
// an "edit" affordance.

function BudgetConversationCard({
  campaignId,
  recommendation,
  initialDailyBudgetCents,
  autopilotOn,
}: {
  campaignId: string;
  recommendation: TakeoverRead['budgetRecommendation'] | null;
  initialDailyBudgetCents: number | null;
  /** When autopilot is ON the founder isn't picking budgets anymore
   *  — mag is. The card switches to a read-only state showing
   *  whichever budget mag will use on the next tick (the founder's
   *  prior pick, or mag's recommendation as the default). The
   *  interactive picker reappears the moment autopilot flips off. */
  autopilotOn: boolean;
}) {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const [persisted, setPersisted] = useState<number | null>(initialDailyBudgetCents);
  const [busy, setBusy] = useState(false);
  const [customMode, setCustomMode] = useState(false);
  const [customInput, setCustomInput] = useState('');

  if (!recommendation) return null;

  const recommendedUsd = recommendation.recommendedDailyBudgetUsdCents / 100;
  const currentUsd = recommendation.currentDailyBudgetUsdCents / 100;
  const persistedUsd = persisted !== null ? persisted / 100 : null;

  // Autopilot-on read-only mode — surface the budget mag will use
  // next tick + the rationale; no interactive picker. Falls back to
  // mag's recommended budget when the founder hasn't locked one in,
  // since that's what the autopilot scheduler reads off magRead.
  if (autopilotOn) {
    const activeUsd = persistedUsd ?? recommendedUsd;
    const activeFormatted = activeUsd.toFixed(0);
    const source = persistedUsd !== null ? t`your pick` : t`mag's pick`;
    return (
      <section className="mt-4 rounded-xl border border-[var(--ink)]/12 bg-[var(--paper)] px-4 py-3 sm:px-5">
        <div className="flex items-baseline justify-between gap-3">
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
            {'↘'} <Trans>daily budget · autopilot</Trans>
          </p>
          <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--ink)]/45">
            {source}
          </span>
        </div>
        <p className="mt-0.5 font-serif text-[14px] leading-[1.4] text-[var(--ink)]/85">
          <Trans>
            mag's running at{' '}
            <span className="font-bold text-[var(--ink)]">${activeFormatted}/day</span> on this
            campaign.
          </Trans>
        </p>
        <p className="mt-2 mag-voice text-[12.5px] leading-[1.4] text-[var(--ink)]/65">
          “{recommendation.rationale}”
        </p>
        <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/45">
          {'↘'} <Trans>flip autopilot off above to change the budget yourself.</Trans>
        </p>
      </section>
    );
  }

  const onPick = async (dailyBudgetUsdCents: number) => {
    if (busy) return;
    setBusy(true);
    try {
      const saved = await setImportedCampaignBudget(campaignId, dailyBudgetUsdCents);
      setPersisted(saved);
      setCustomMode(false);
      setCustomInput('');
      const savedUsd = (saved / 100).toFixed(0);
      magToast.success(t`budget noted.`, t`i'll use $${savedUsd}/day when autopilot's on.`);
      // Re-warm the imported detail query so a refresh shows the
      // same persisted value without a refetch round-trip.
      await queryClient.invalidateQueries({ queryKey: ['imported-campaign', campaignId] });
    } catch (err) {
      magToast.error("couldn't save the budget pick.", founderErrorCopy(err, 'try again.'));
    } finally {
      setBusy(false);
    }
  };

  const onCustomSubmit = async () => {
    const parsed = Math.round(parseFloat(customInput) * 100);
    if (!Number.isFinite(parsed) || parsed < 100 || parsed > 500_000) {
      magToast.error(t`out of range.`, t`pick something between $1 and $5,000 per day.`);
      return;
    }
    await onPick(parsed);
  };

  // Locked-in state — founder already picked. Compact receipt with
  // an edit button to reopen the choices.
  if (persistedUsd !== null && !customMode) {
    const persistedUsdFormatted = persistedUsd.toFixed(0);
    return (
      <section className="mt-4 flex items-center justify-between rounded-xl border border-[var(--ink)]/12 bg-[var(--paper)] px-4 py-3 sm:px-5">
        <div>
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
            {'↘'} <Trans>daily budget</Trans>
          </p>
          <p className="mt-0.5 font-serif text-[14px] leading-[1.4] text-[var(--ink)]/80">
            <Trans>${persistedUsdFormatted}/day. i'll use this when autopilot's on.</Trans>
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setCustomMode(true);
            setCustomInput(persistedUsd.toFixed(0));
          }}
          className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.08em] text-[var(--ink)]/60 hover:text-[var(--ink)]"
        >
          <Trans>edit</Trans>
        </button>
      </section>
    );
  }

  const currentFormatted = currentUsd.toFixed(0);
  return (
    <section className="mt-4 rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-5 shadow-[3px_3px_0_var(--spell)] sm:p-6">
      <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
        {'↘'} <Trans>mag's budget recommendation</Trans>
      </p>
      <p className="mt-2 mag-voice text-[15px] leading-[1.45] text-[var(--ink)]">
        “{recommendation.rationale}”
      </p>
      <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-3">
        <BudgetOptionTile
          eyebrow={t`mag's pick`}
          amount={recommendedUsd}
          note={
            recommendedUsd < currentUsd
              ? t`down from $${currentFormatted}`
              : recommendedUsd > currentUsd
                ? t`up from $${currentFormatted}`
                : t`same as current`
          }
          primary
          disabled={busy}
          onPick={() => onPick(recommendation.recommendedDailyBudgetUsdCents)}
        />
        <BudgetOptionTile
          eyebrow={t`keep current`}
          amount={currentUsd}
          note={t`what you're running today`}
          disabled={busy}
          onPick={() => onPick(recommendation.currentDailyBudgetUsdCents)}
        />
        <BudgetOptionTile
          eyebrow={t`something else`}
          amount={null}
          note={t`set a custom daily cap`}
          disabled={busy}
          onPick={() => setCustomMode(true)}
        />
      </div>
      {customMode ? (
        <div className="mt-4 flex flex-col gap-2 rounded-xl border-[1.5px] border-dashed border-[var(--ink)]/30 bg-[var(--paper)] p-4 sm:flex-row sm:items-center">
          <label className="flex flex-1 items-center gap-2 font-mono text-[11px] uppercase tracking-[0.08em] text-[var(--ink)]/65">
            $
            <input
              type="number"
              inputMode="decimal"
              min={1}
              max={5000}
              step={1}
              value={customInput}
              onChange={(e) => setCustomInput(e.target.value)}
              placeholder="20"
              // biome-ignore lint/a11y/noAutofocus: the input only mounts when the founder toggles into customMode — focus follows their click, typing should be the immediate next action
              autoFocus
              disabled={busy}
              className="flex-1 rounded-md border border-[var(--ink)]/20 bg-white px-3 py-1.5 font-mono text-[14px] text-[var(--ink)] outline-none focus:border-[var(--ink)]"
            />
            <span className="text-[var(--ink)]/55">
              <Trans>/ day</Trans>
            </span>
          </label>
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setCustomMode(false);
                setCustomInput('');
              }}
              disabled={busy}
              className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.08em] text-[var(--ink)]/55 hover:text-[var(--ink)]"
            >
              <Trans>cancel</Trans>
            </button>
            <button
              type="button"
              onClick={onCustomSubmit}
              disabled={busy || customInput.trim().length === 0}
              className="btn btn-sm disabled:opacity-50"
            >
              {busy ? t`saving…` : t`use this`}
              <span className="btn-arrow">→</span>
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}

function BudgetOptionTile({
  eyebrow,
  amount,
  note,
  primary,
  disabled,
  onPick,
}: {
  eyebrow: string;
  amount: number | null;
  note: string;
  primary?: boolean;
  disabled: boolean;
  onPick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onPick}
      disabled={disabled}
      className={`flex flex-col gap-1 rounded-xl border-[1.5px] p-3 text-left transition disabled:cursor-not-allowed disabled:opacity-60 ${
        primary
          ? 'border-[var(--ink)] bg-[var(--spell)] shadow-[2px_2px_0_var(--plum)] hover:shadow-[3px_3px_0_var(--plum)]'
          : 'border-[var(--ink)]/20 bg-white hover:border-[var(--ink)] hover:shadow-[2px_2px_0_var(--ink)]'
      }`}
    >
      <span className="font-mono text-[9.5px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
        ↘ {eyebrow}
      </span>
      {amount !== null ? (
        <span className="font-display text-[22px] font-extrabold leading-none tracking-[-0.02em] text-[var(--ink)]">
          ${amount.toFixed(0)}
          <span className="ml-1 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--ink)]/55">
            <Trans>/day</Trans>
          </span>
        </span>
      ) : (
        <span className="font-display text-[20px] font-extrabold leading-none tracking-[-0.02em] text-[var(--ink)]/80">
          <Trans>custom</Trans>
        </span>
      )}
      <span className="font-mono text-[10px] uppercase tracking-[0.06em] text-[var(--ink)]/55">
        {note}
      </span>
    </button>
  );
}

// ─── KPI band — "is this campaign winning?" ───────────────────────────
//
// Three numbers above the autopilot toggle so the founder reads the
// last-7d performance at a glance before deciding whether to approve
// anything. Spend is the always-populated anchor; clicks tracks
// reach; conversions is the goal metric. Each tile shows the 7d
// value + a WoW delta (vs days 8-14 ago). When the snapshot's
// kpiBand is missing (legacy imports pre-extension) we collapse to a
// "not enough history yet" line so the surface doesn't render a
// suspicious row of zeros.
//
// Color rule: green when "better" (more spend isn't itself good, but
// up-and-to-the-right reach/conversions is), red when "worse." For
// spend, no color — direction is ambiguous in isolation.

function KpiBand({ snapshot }: { snapshot: CampaignSnapshot }) {
  const { t } = useLingui();
  const band = snapshot.kpiBand;
  if (!band) {
    return (
      <section className="mt-6 rounded-xl border border-[var(--ink)]/12 bg-[var(--paper)] px-5 py-3">
        <p className="font-mono text-[10.5px] uppercase tracking-[0.12em] text-[var(--ink)]/55">
          {'↘'}{' '}
          <Trans>
            performance · not enough history yet — i'll have the week-over-week call next week.
          </Trans>
        </p>
      </section>
    );
  }
  const { last7d, prior7d } = band;
  return (
    <section className="mt-6 grid grid-cols-1 gap-2 sm:grid-cols-3">
      <KpiTile
        label={t`spend · 7d`}
        value={`$${formatNum(last7d.costUsd, 0)}`}
        prior={prior7d.costUsd}
        current={last7d.costUsd}
        intent="neutral"
      />
      <KpiTile
        label={t`clicks · 7d`}
        value={formatNum(last7d.clicks, 0)}
        prior={prior7d.clicks}
        current={last7d.clicks}
        intent="up-is-good"
      />
      <KpiTile
        label={t`conversions · 7d`}
        value={
          last7d.conversions === 0 && prior7d.conversions === 0
            ? '0'
            : formatNum(last7d.conversions, 1)
        }
        prior={prior7d.conversions}
        current={last7d.conversions}
        intent="up-is-good"
        caption={
          last7d.conversions === 0 && prior7d.conversions === 0
            ? t`no tracking — i can't measure this yet.`
            : null
        }
      />
    </section>
  );
}

function KpiTile({
  label,
  value,
  prior,
  current,
  intent,
  caption,
}: {
  label: string;
  value: string;
  prior: number;
  current: number;
  intent: 'up-is-good' | 'neutral';
  caption?: string | null;
}) {
  const { t } = useLingui();
  const delta = computeDelta(prior, current);
  return (
    <article className="rounded-xl border border-[var(--ink)]/12 bg-[var(--paper)] px-4 py-3">
      <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--ink)]/55">
        ↘ {label}
      </p>
      <div className="mt-1 flex items-baseline gap-2">
        <span className="font-display text-[24px] font-black leading-none tracking-[-0.03em] text-[var(--ink)]">
          {value}
        </span>
        {delta !== null ? (
          <span
            className={`font-mono text-[11px] font-semibold tabular-nums ${deltaColor(delta, intent)}`}
          >
            {delta > 0 ? '+' : ''}
            {delta.toFixed(0)}%
          </span>
        ) : null}
      </div>
      <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--ink)]/45">
        {caption ?? t`vs prior 7d`}
      </p>
    </article>
  );
}

function computeDelta(prior: number, current: number): number | null {
  if (prior === 0 && current === 0) return null;
  if (prior === 0) return current > 0 ? 100 : 0;
  return ((current - prior) / prior) * 100;
}

function deltaColor(delta: number, intent: 'up-is-good' | 'neutral'): string {
  if (intent === 'neutral') return 'text-[var(--ink)]/65';
  if (delta > 0) return 'text-[color:var(--sage)]';
  if (delta < 0) return 'text-[color:var(--pulse)]';
  return 'text-[var(--ink)]/55';
}

function formatNum(n: number, fractionDigits: number): string {
  return formatNumber(n, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}

// ─── Pause on google — founder-direct ───────────────────────────────
// Writes ENABLED ↔ PAUSED to google ads without going through mag's
// recommendation flow. Same setGoogleCampaignStatus mutation the
// takeover ActionItem fires; we synthesize an actionContext so the
// takeover_action_executions audit log captures the founder's choice
// the same way it captures recommended-then-approved actions. The
// pause is reversible (resume button surfaces when status='paused')
// — no typed confirmation because the action itself is reversible
// and the founder reaches for this surface specifically to flip
// state.

function PauseOnGoogleSection({
  campaignId,
  brandId,
  snapshot,
  setSnapshot,
}: {
  campaignId: string;
  brandId: string;
  snapshot: CampaignSnapshot;
  setSnapshot: React.Dispatch<React.SetStateAction<CampaignSnapshot>>;
}) {
  const { t } = useLingui();
  const [busy, setBusy] = useState(false);
  const isPaused = snapshot.status === 'paused';
  const statusWord = isPaused ? t`paused` : t`serving`;
  const isRemoved = snapshot.status === 'removed';
  // Removed campaigns can't be re-enabled via api per google's
  // status rules — surface a read-only line instead of a dead button.
  if (isRemoved) {
    return (
      <article className="mt-8 rounded-2xl border-[1.5px] border-[var(--ink)]/15 bg-white p-5 sm:p-6">
        <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--ink)]/55">
          {'↘'} <Trans>campaign status</Trans>
        </p>
        <p className="mt-2 mag-voice text-[14px] leading-[1.5] text-[var(--ink)]/60">
          <Trans>
            “this campaign is set to removed in google ads — i can't flip it back on from here.
            you'd need to clone it inside google ads if you want it serving again.”
          </Trans>
        </p>
      </article>
    );
  }

  const handleFlip = async () => {
    if (busy) return;
    setBusy(true);
    const nextStatus = isPaused ? 'ENABLED' : 'PAUSED';
    const nextSnapshotStatus = isPaused ? ('enabled' as const) : ('paused' as const);
    const verb = isPaused ? 'resume' : 'pause';
    try {
      const result = await setGoogleCampaignStatus(
        brandId,
        snapshot.providerCampaignId,
        nextStatus,
        {
          importedCampaignId: campaignId,
          title: `${verb} the campaign on google`,
          rationale:
            'founder-direct action from the takeover view — not a mag recommendation. logged for audit.',
          execution: {
            kind: isPaused ? 'enable_campaign' : 'pause_campaign',
            resourceId: snapshot.providerCampaignId,
          },
        },
      );
      magToast.success(
        isPaused ? t`resumed on google.` : t`paused on google.`,
        isPaused
          ? t`campaign's serving again — i'll pick up new spend on the next snapshot refresh.`
          : t`campaign keeps existing in google, just not spending. flip back any time.`,
      );
      setSnapshot((s) => ({ ...s, status: nextSnapshotStatus }));
      // Hint that result was via mock or real — useful when debugging
      // in dev; production never reads this.
      void result.via;
    } catch (err) {
      const detail = founderErrorCopy(err, 'try again.');
      // Detect google's per-developer-token quota exhaustion — the
      // error string carries either the gRPC code or the verbatim
      // "Resource has been exhausted" phrasing. Surface a mag-voice
      // line with a concrete next step instead of the raw gRPC text.
      const isQuotaExhausted = /RESOURCE_EXHAUSTED|Resource has been exhausted|quota/i.test(detail);
      if (isQuotaExhausted) {
        magToast.error(
          isPaused
            ? t`google's rate-limiting us right now.`
            : t`couldn't pause — google's rate-limiting us right now.`,
          t`i'll retry on the next mag check. if you need this stopped now, flip it in google ads directly.`,
        );
      } else {
        magToast.error(isPaused ? t`couldn't resume.` : t`couldn't pause.`, detail);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <article className="mt-8 rounded-2xl border-[1.5px] border-[var(--ink)]/15 bg-white p-5 sm:p-6">
      <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
        {'↘'} <Trans>campaign status</Trans>
      </p>
      <p className="mt-2 mag-voice text-[14px] leading-[1.5] text-[var(--ink)]/75">
        {isPaused
          ? t`“this campaign is paused on google — no spend, no impressions. flip it back on whenever you are ready.”`
          : t`“paused campaigns stop spending immediately but stay in google ads — you can resume any time without losing history.”`}
      </p>
      <div className="mt-4 flex items-center justify-between gap-3">
        <p className="font-mono text-[10.5px] uppercase tracking-[0.1em] text-[var(--ink)]/55">
          {'↘'} <Trans>currently {statusWord}</Trans>
        </p>
        <button
          type="button"
          onClick={handleFlip}
          disabled={busy}
          className={`btn ${isPaused ? 'btn-spell' : 'btn-ghost'} disabled:cursor-not-allowed disabled:opacity-50`}
        >
          {busy
            ? isPaused
              ? t`resuming…`
              : t`pausing…`
            : isPaused
              ? t`resume on google`
              : t`pause on google`}
          {!busy ? <span className="btn-arrow">{isPaused ? '↗' : '↘'}</span> : null}
        </button>
      </div>
    </article>
  );
}

// ─── Remove from magig — danger zone ─────────────────────────────────
// Stops magig from tracking this campaign. Does NOT touch the platform
// campaign — the founder pauses or deletes on Google/Meta themselves
// via their own UI. Type-to-confirm matches the workspace-delete
// pattern from [12.2L-53] so a stray click on a 30-day-old takeover
// can't wipe the row.

function DeleteImportedCampaignSection({
  campaignId,
  slug,
  name,
}: {
  campaignId: string;
  slug: string;
  name: string;
}) {
  const { t } = useLingui();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [deleting, setDeleting] = useState(false);

  const target = name.trim();
  const matches = typed.trim() === target && target.length > 0;

  const handleDelete = async () => {
    if (!matches || deleting) return;
    setDeleting(true);
    try {
      await deleteImportedCampaign(campaignId);
      magToast.success(
        t`removed from magig.`,
        t`i'll stop tracking it. the campaign keeps running on google until you pause it there.`,
      );
      // Wipe the cached queries that referenced this campaign so the
      // list refresh on the next page picks up the absence.
      await queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      await queryClient.invalidateQueries({ queryKey: ['imported-campaigns'] });
      void navigate({ to: '/brand/$slug/campaigns', params: { slug } });
    } catch (err) {
      magToast.error("couldn't remove it.", founderErrorCopy(err, 'try again.'));
      setDeleting(false);
    }
  };

  return (
    <article className="mt-8 rounded-2xl border-[1.5px] border-[var(--pulse)]/40 bg-[var(--pulse)]/4 p-5 sm:p-6">
      <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--pulse)]">
        {'↘'} <Trans>remove from magig</Trans>
      </p>
      <p className="mt-2 mag-voice text-[14px] leading-[1.5] text-[var(--ink)]/75">
        <Trans>
          “i'll stop tracking this campaign and clear what i've read so far. it'll keep running on
          google — pause or delete it there if you want it actually stopped.”
        </Trans>
      </p>
      {!confirmOpen ? (
        <div className="mt-4 flex justify-end">
          <button type="button" onClick={() => setConfirmOpen(true)} className="btn btn-pulse">
            <Trans>remove from magig</Trans>
            <span className="btn-arrow">→</span>
          </button>
        </div>
      ) : (
        <div className="mt-4 flex flex-col gap-3 rounded-xl border border-[var(--pulse)]/30 bg-white p-4">
          <p className="font-serif text-[13.5px] leading-[1.5] text-[var(--ink)]/80">
            <Trans>
              type <span className="font-mono font-bold text-[var(--ink)]">{target}</span> below to
              confirm. you can re-import any time from /campaigns/import.
            </Trans>
          </p>
          <input
            type="text"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={target}
            disabled={deleting}
            // biome-ignore lint/a11y/noAutofocus: explicit click opens this — typing IS the next intended action.
            autoFocus
            className="rounded-md border-[1.5px] border-[var(--line-strong)] bg-white px-3 py-2 text-[14px] outline-none focus:border-[var(--pulse)]"
          />
          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => {
                setConfirmOpen(false);
                setTyped('');
              }}
              disabled={deleting}
              className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.08em] text-[var(--ink)]/60 hover:text-[var(--ink)] disabled:opacity-50"
            >
              <Trans>cancel</Trans>
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={!matches || deleting}
              className="btn btn-pulse-solid disabled:cursor-not-allowed disabled:opacity-50"
            >
              {deleting ? t`removing…` : t`remove forever`}
              {!deleting ? <span className="btn-arrow">→</span> : null}
            </button>
          </div>
        </div>
      )}
    </article>
  );
}

/** [spec move-campaign-between-sharing-brands] Move this campaign to a sibling
 *  brand that shares its ad account.
 *
 *  RENDERED ONLY WHEN THE ACCOUNT IS ACTUALLY SHARED. `sharedWith` is empty for
 *  a single-brand account, which is almost all of them, and the caller renders
 *  nothing at all in that case — a disabled control on every imported campaign
 *  would charge every founder's attention to serve a handful.
 *
 *  NO TYPE-TO-CONFIRM, unlike the delete section directly below. Deleting is
 *  irreversible, which is what earns that ceremony; a move is undone by moving
 *  back, and the endpoint is idempotent. Adding a confirmation here would train
 *  founders to type through confirmations, which is exactly what makes the
 *  delete one work.
 *
 *  THE COPY SAYS THE SPEND MOVES, and it has to: the per-brand autonomous spend
 *  cap is DERIVED from attributed spend, so a founder who thinks they are
 *  correcting a label is also changing what mag may spend alone on two brands. */
function MoveCampaignBrandSection({
  campaignId,
  sharedWith,
}: {
  campaignId: string;
  sharedWith: Array<{ id: string; name: string; slug: string }>;
}) {
  const { t } = useLingui();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [targetId, setTargetId] = useState(sharedWith[0]?.id ?? '');
  const [moving, setMoving] = useState(false);

  const target = sharedWith.find((b) => b.id === targetId) ?? sharedWith[0];
  if (!target) return null;

  const handleMove = async () => {
    if (moving) return;
    setMoving(true);
    try {
      await moveImportedCampaignToBrand(campaignId, target.id);
      magToast.success(
        t`moved to ${target.name}.`,
        t`its spend and its numbers go with it. move it back any time.`,
      );
      await queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      await queryClient.invalidateQueries({ queryKey: ['imported-campaigns'] });
      // This page lives under the OLD brand's slug and its loader redirects
      // when the campaign is not found for that brand — so staying would break
      // on the next refresh. Land on the same campaign where it now lives.
      void navigate({
        to: '/brand/$slug/campaigns/imported/$id',
        params: { slug: target.slug, id: campaignId },
      });
    } catch (err) {
      magToast.error("couldn't move it.", founderErrorCopy(err, 'try again.'));
      setMoving(false);
    }
  };

  return (
    <article className="mt-8 rounded-2xl border-[1.5px] border-[var(--ink)]/20 bg-white p-5 sm:p-6">
      <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
        {'↘'} <Trans>wrong brand?</Trans>
      </p>
      <p className="mt-2 mag-voice text-[14px] leading-[1.5] text-[var(--ink)]/75">
        <Trans>
          “this ad account is shared with another brand. if this campaign is really theirs, move it
          — its spend and its numbers go with it.”
        </Trans>
      </p>
      <div className="mt-4 flex flex-wrap items-center justify-end gap-3">
        <select
          value={target.id}
          onChange={(e) => setTargetId(e.target.value)}
          disabled={moving}
          aria-label={t`brand to move this campaign to`}
          className="rounded-md border-[1.5px] border-[var(--line-strong)] bg-white px-3 py-2 text-[14px] outline-none focus:border-[var(--plum)] disabled:opacity-50"
        >
          {sharedWith.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={handleMove}
          disabled={moving}
          className="btn btn-ink disabled:cursor-not-allowed disabled:opacity-50"
        >
          {moving ? t`moving…` : t`move it`}
          {!moving ? <span className="btn-arrow">→</span> : null}
        </button>
      </div>
    </article>
  );
}

/** Empty CampaignMetrics — used for optimistic inserts on writes
 *  where the platform hasn't reported any data yet. */
function emptyMetrics(): CampaignMetrics {
  return {
    windowDays: 30,
    impressions: 0,
    clicks: 0,
    ctr: 0,
    conversions: 0,
    conversionRate: 0,
    costUsd: 0,
    costPerConversionUsd: 0,
    daysBudgetCapped: 0,
  };
}

/** Pulls a numeric id out of a Google Ads resource name array using a
 *  pattern that matches a specific entity type — e.g.
 *  `customers/123/adGroups/456` → `'456'` with /\/adGroups\/(\d+)$/. */
function extractIdFromResourceName(
  resourceNames: readonly string[],
  pattern: RegExp,
): string | null {
  for (const name of resourceNames) {
    const match = pattern.exec(name);
    if (match?.[1]) return match[1];
  }
  return null;
}

/** Conditional NewRsaModal wrapper — resolves the ad-group's label
 *  from the snapshot only when we have a target, so the modal's
 *  open state is purely a function of `target !== null`. When the
 *  target carries a `seed` (mag's draft), we pass it through to
 *  pre-fill the form. */
function NewRsaTrigger({
  snapshot,
  target,
  brandId,
  onClose,
  onCreated,
}: {
  snapshot: CampaignSnapshot;
  target: NewRsaTarget | null;
  brandId: string;
  onClose: () => void;
  onCreated: (newAdProviderId: string, headlines: string[], descriptions: string[]) => void;
}) {
  if (!target) {
    return (
      <NewRsaModal
        open={false}
        brandId={brandId}
        adGroupResourceId=""
        adGroupLabel=""
        onCancel={onClose}
        onCreated={() => {}}
      />
    );
  }
  const adGroup = snapshot.adGroups.find((ag) => ag.providerAdGroupId === target.adGroupResourceId);
  // Seed precedence: mag's draft > campaign-level finalUrl from
  // providerRaw > "https://" placeholder.
  const seedFinalUrl =
    target.seed?.finalUrl ??
    (snapshot.providerRaw?.['finalUrl'] as string | undefined) ??
    'https://';
  return (
    <NewRsaModal
      open={true}
      brandId={brandId}
      adGroupResourceId={target.adGroupResourceId}
      adGroupLabel={adGroup?.name ?? 'this ad group'}
      seedHeadlines={target.seed?.headlines ?? []}
      seedDescriptions={target.seed?.descriptions ?? []}
      seedFinalUrl={seedFinalUrl}
      actionContext={target.actionContext}
      onCancel={onClose}
      onCreated={(result, submitted) => {
        const newAdResourceId =
          extractIdFromResourceName(result.resourceNames, /\/adGroupAds\/(\d+)$/) ??
          `pending-${Date.now()}`;
        const mockBadge = result.via === 'mock' ? ' (mock)' : '';
        magToast.success(
          `new rsa shipped${mockBadge}.`,
          'rotating in alongside the existing ads. metrics show on the next scan.',
        );
        onCreated(newAdResourceId, submitted.headlines, submitted.descriptions);
        onClose();
      }}
    />
  );
}

/** Renders the modal only when there's a target ad; resolves the
 *  seed values + ad-group id from the snapshot. Lives as its own
 *  component so the SnapshotColumn body stays tidy. */
function RsaSwapTrigger({
  snapshot,
  target,
  brandId,
  onClose,
  onSwapped,
}: {
  snapshot: CampaignSnapshot;
  target: SwapTarget | null;
  brandId: string;
  onClose: () => void;
  onSwapped: (
    oldAdId: string,
    newAdProviderId: string,
    newHeadlines: string[],
    newDescriptions: string[],
  ) => void;
}) {
  if (!target) {
    return (
      <RsaSwapModal
        open={false}
        brandId={brandId}
        oldAdGroupAdResourceId=""
        adGroupResourceId=""
        initialHeadlines={[]}
        initialDescriptions={[]}
        initialFinalUrl=""
        adLabel=""
        onCancel={onClose}
        onSwapped={() => {}}
      />
    );
  }
  const targetAdId = target.adId;
  const targetAd = snapshot.ads.find((a) => a.providerAdId === targetAdId);
  // [12.2L-116] Prefer the ad's own providerAdGroupId (populated on
  // snapshots fetched after the Google client extension landed).
  // Falls back to the campaign's first ad group for legacy snapshots
  // — correct on single-ad-group campaigns, wrong on multi-ad-group
  // ones (the older v1 limitation; gone the moment a snapshot
  // refresh runs).
  const adGroupResourceId =
    targetAd?.providerAdGroupId ?? snapshot.adGroups[0]?.providerAdGroupId ?? '';
  // marketing_url isn't on the snapshot; use the campaign's
  // providerCampaignId-derived placeholder. Founder edits the field
  // before submit; the placeholder just stops the URL input rendering
  // empty + the validate-gate catching the immediate error.
  const fallbackFinalUrl = (snapshot.providerRaw?.['finalUrl'] as string | undefined) ?? 'https://';
  if (!targetAd) {
    return (
      <RsaSwapModal
        open={false}
        brandId={brandId}
        oldAdGroupAdResourceId=""
        adGroupResourceId=""
        initialHeadlines={[]}
        initialDescriptions={[]}
        initialFinalUrl=""
        adLabel=""
        onCancel={onClose}
        onSwapped={() => {}}
      />
    );
  }
  // [12.2L-110] Pre-fill priority: when the trigger arrived with mag's
  // draft (action approve on a swap_rsa action that carried draft
  // copy), the founder sees what mag would ship. Without a draft
  // (founder clicked "swap" on an existing ad row), fall back to the
  // ad's existing copy so the founder can hand-rewrite.
  const initialHeadlines: RsaHeadlineSeed[] = target.draft?.headlines ?? targetAd.headlines;
  const initialDescriptions: RsaDescriptionSeed[] =
    target.draft?.descriptions ?? targetAd.descriptions;
  const initialFinalUrl = target.draft?.finalUrl ?? fallbackFinalUrl;
  const draftMode = target.draft !== null;
  return (
    <RsaSwapModal
      open={true}
      brandId={brandId}
      oldAdGroupAdResourceId={targetAdId}
      adGroupResourceId={adGroupResourceId}
      initialHeadlines={initialHeadlines}
      initialDescriptions={initialDescriptions}
      initialFinalUrl={initialFinalUrl}
      adLabel={targetAd.name}
      draftMode={draftMode}
      actionContext={target.actionContext}
      onCancel={onClose}
      onSwapped={async (result) => {
        // Extract the new ad's numeric id from the second resource
        // name returned by the mutation (remove + create chain;
        // [0] is the old removal, [1] is the new ad).
        const newAdResourceName = result.resourceNames[1] ?? '';
        const match = /\/adGroupAds\/(\d+)$/.exec(newAdResourceName);
        const newAdProviderId = match?.[1] ?? newAdResourceName;
        const mockBadge = result.via === 'mock' ? ' (mock)' : '';
        magToast.success(
          `rsa swapped${mockBadge}.`,
          result.via === 'mock'
            ? "the takeover snapshot's updated locally — connect the dev token + flip the real client to ship for real."
            : 'live on google ads. ad-strength + metrics reset on the new ad.',
        );
        // For the local-state insert we need the new copy. Read it
        // off targetAd's CURRENT inputs from the modal — but the
        // modal owns those. Simplest: pass the OLD headlines as a
        // best-guess for the placeholder; the snapshot refresh on
        // next import will hydrate the real ones.
        onSwapped(targetAdId, newAdProviderId, targetAd.headlines, targetAd.descriptions);
        onClose();
      }}
    />
  );
}

// ─── Mag's read ──────────────────────────────────────────────────────────

function MagReadColumn({
  campaignId,
  read,
  campaignName,
  slug,
  brandId,
  snapshot,
  setSnapshot,
  setMagReadState,
  writesAvailable,
  autopilotOn,
  onOpenSwap,
  onOpenNewRsaWithSeed,
  onOpenNewAdGroupWithSeed,
  onAfterWrite,
  onRefreshMagRead,
  magReadRefreshing,
}: {
  campaignId: string;
  read: TakeoverRead | null;
  campaignName: string;
  slug: string;
  /** s207 — wired so ActionItem's "approve" buttons can dispatch
   *  the matching Phase B write or open the swap modal.
   *  s209: + the two new create-modal openers, which carry mag's
   *  draft through as a seed for pre-fill. */
  brandId: string;
  snapshot: CampaignSnapshot;
  setSnapshot: React.Dispatch<React.SetStateAction<CampaignSnapshot>>;
  /** Used by the dismiss path to drop the dismissed action from the
   *  in-memory magRead so the founder doesn't have to wait for the
   *  background refresh to see it disappear. */
  setMagReadState: React.Dispatch<React.SetStateAction<TakeoverRead | null>>;
  writesAvailable: boolean;
  /** [12.2L-95] drives the action-list footnote so it doesn't claim
   *  "nothing autonomous" while the scheduler is about to act. */
  autopilotOn: boolean;
  /** [12.2L-110] swap target carries an optional draft so action
   *  approves can pre-fill the modal with mag's draft rewrite. */
  onOpenSwap: (target: SwapTarget) => void;
  onOpenNewRsaWithSeed: (target: NewRsaTarget) => void;
  onOpenNewAdGroupWithSeed: (target: NewAdGroupTarget) => void;
  /** Background snapshot + magRead refresh — fired by ActionItem
   *  after each successful direct-execute write (pause / enable /
   *  budget). Swaps + creates fire it from their modal closures. */
  onAfterWrite: () => Promise<void>;
  /** Manual "give me a fresh read" — shown when autopilot is OFF
   *  since auto-refresh of mag's read is suppressed in that mode. */
  onRefreshMagRead: () => Promise<void>;
  magReadRefreshing: boolean;
}) {
  const { t } = useLingui();
  // Executions list — keyed by signature so each ActionItem can
  // look up its own ✓ shipped impact panel. Refetched after every
  // refresh of mag's read (so a brand-new approval shows its panel
  // as soon as the snapshot catches up). Cached at 30s.
  const executionsQ = useQuery({
    queryKey: ['imported-campaign', campaignId, 'executions'],
    queryFn: () => listTakeoverExecutions(campaignId),
    staleTime: 30_000,
  });
  const executionsBySignature = new Map<string, TakeoverExecutionItem>();
  for (const item of executionsQ.data ?? []) {
    // Multiple executions per signature can exist; keep the latest
    // (the list comes from the api newest-first, so the FIRST
    // encountered is the freshest).
    if (!executionsBySignature.has(item.execution.actionSignature)) {
      executionsBySignature.set(item.execution.actionSignature, item);
    }
  }

  if (!read) {
    return (
      <article className="rounded-2xl border-[1.5px] border-[var(--line-strong)] bg-white p-5 shadow-[4px_4px_0_var(--plum)]">
        <div className="flex items-start gap-3">
          <Mag size={36} mood="confused" />
          <div>
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
              {'↘'} <Trans>mag's read</Trans>
            </p>
            <p className="mt-1.5 font-serif text-[18px] leading-tight text-[var(--ink)]">
              <Trans>
                “i could read the snapshot, but i need your brief filled in to say what i'd change.”
              </Trans>
            </p>
            <Link
              to="/brand/$slug/brief"
              params={{ slug }}
              className="mt-3 inline-block link-plum text-[13px]"
            >
              {'↘'} <Trans>finish the brief</Trans> {'→'}
            </Link>
          </div>
        </div>
      </article>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Brand-aligned mag-speaks card — white bg + plum shadow,
          default-palette Mag (ink body with yellow eyes, her
          identity), ink body text for readability. The earlier
          dark-ink + yellow-on-black variant felt loud and was hard
          to read on multi-paragraph headlines; flipping to the
          standard mag-surface card pattern matches the brand-
          drafts review and the autopilot card from [12.2L-90]. */}
      <article className="rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-5 shadow-[4px_4px_0_var(--plum)] sm:p-6">
        <div className="flex items-start gap-3">
          <Mag size={48} mood="smug" />
          <div className="min-w-0 flex-1">
            <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
              {'↘'} <Trans>mag's read · {campaignName}</Trans>
            </p>
            <p className="mt-2 mag-voice text-[17px] leading-[1.4] text-[var(--ink)]/85">
              “{read.headline}”
            </p>
            <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--ink)]/55">
              ↘ {read.shortTitle}
            </p>
          </div>
        </div>
      </article>

      {/* "what's working" + "what's not" used to live here as
          bulleted diagnostic blocks — useful for an ads expert,
          noisy for a founder. Dropped: the per-action rationale on
          each "what i'd do" row carries the same signal in
          context. If a founder wants the raw read, Mag's headline
          card above is the one-line verdict. */}

      {/* Campaign import reviewer findings — deterministic red-flag
          pass that ran alongside the takeover-read agent at import.
          Surfaces what's broken BEFORE "what to do about it" so the
          founder reads diagnosis before prescription. Hidden when
          no findings — empty = clean campaign, good. */}
      {read.findings && read.findings.length > 0 ? (
        <FindingsSection findings={read.findings} />
      ) : null}

      {(() => {
        // When autopilot is ON, mag is taking the wheel — the page
        // inverts from "what i'd do (please approve)" to "what i've
        // done (here's the record)." The pending-recommendation
        // list disappears entirely; mag will ship whatever's on her
        // read in the next tick and the founder reads after the
        // fact via the executed-actions history.
        if (autopilotOn) {
          // Edge case: even with autopilot on, some actions can't be
          // auto-applied — agent opted out (autoApplyEligible: false)
          // or swap_rsa without a draft (no copy to push). These
          // would silently sit forever if we hid them alongside the
          // autopilot-eligible ones. Surface them in a "still needs
          // your eye" strip above the history so the founder can
          // approve manually OR dismiss.
          const needsEyeActions = read.actions.filter(
            (a) => a.execution !== undefined && !isAutoApplyEligible(a),
          );
          return (
            <>
              {needsEyeActions.length > 0 ? (
                <NeedsYourEyeStrip
                  actions={needsEyeActions}
                  campaignId={campaignId}
                  brandId={brandId}
                  snapshot={snapshot}
                  setSnapshot={setSnapshot}
                  setMagReadState={setMagReadState}
                  writesAvailable={writesAvailable}
                  onOpenSwap={onOpenSwap}
                  onOpenNewRsaWithSeed={onOpenNewRsaWithSeed}
                  onOpenNewAdGroupWithSeed={onOpenNewAdGroupWithSeed}
                  onAfterWrite={onAfterWrite}
                  executionsBySignature={executionsBySignature}
                />
              ) : null}
              <ExecutedActionsHistory
                executions={executionsQ.data ?? []}
                loading={executionsQ.isLoading}
              />
            </>
          );
        }

        // Autopilot OFF — founder is in the driver's seat, surface
        // pending recommendations with approve / dismiss controls.
        // [12.2L-108] Text-only actions (no execution block) are
        // persisted as followups at import time — surface them in the
        // founder's followups inbox, not as silent rows on the
        // takeover view. Filter them out here so each surface owns
        // its mental model: takeover = "things mag does to ads",
        // followups = "things you do outside ads".
        const platformActions = read.actions.filter((a) => a.execution !== undefined);
        const actionCount = platformActions.length;
        const followupCount = read.actions.length - platformActions.length;
        if (platformActions.length === 0 && followupCount === 0) return null;
        return (
          <article className="rounded-2xl border-[1.5px] border-[var(--line-strong)] bg-white p-5 shadow-[4px_4px_0_var(--plum)]">
            <div className="flex items-baseline justify-between gap-3">
              <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
                {'↘'} <Trans>what i'd do · {actionCount}</Trans>
              </p>
              <div className="flex items-baseline gap-3">
                <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--ink)]/55">
                  <Trans>in order</Trans>
                </span>
                <button
                  type="button"
                  onClick={() => void onRefreshMagRead()}
                  disabled={magReadRefreshing}
                  className="font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--plum)] transition-colors duration-150 hover:text-[var(--ink)] disabled:opacity-50"
                  title={t`re-run mag's read against the current campaign state`}
                >
                  {'↻'} {magReadRefreshing ? t`refreshing…` : t`refresh mag's read`}
                </button>
              </div>
            </div>
            {platformActions.length > 0 ? (
              <ol className="mt-4 flex flex-col gap-3">
                {platformActions.map((a, i) => {
                  const sig = a.execution ? takeoverActionSignature(a.execution) : null;
                  const matching = sig ? executionsBySignature.get(sig) : undefined;
                  return (
                    <li
                      key={a.title}
                      className="flex gap-3 rounded-xl border border-[var(--ink)]/12 bg-[var(--paper)] p-4 transition-colors duration-150 hover:border-[var(--ink)]/22 sm:gap-4 sm:p-5"
                    >
                      <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full border-[1.5px] border-[var(--ink)] bg-[var(--spell)] font-mono text-[10px] font-bold">
                        {i + 1}
                      </span>
                      <ActionItem
                        campaignId={campaignId}
                        action={a}
                        brandId={brandId}
                        snapshot={snapshot}
                        setSnapshot={setSnapshot}
                        setMagReadState={setMagReadState}
                        writesAvailable={writesAvailable}
                        onOpenSwap={onOpenSwap}
                        onOpenNewRsaWithSeed={onOpenNewRsaWithSeed}
                        onOpenNewAdGroupWithSeed={onOpenNewAdGroupWithSeed}
                        onAfterWrite={onAfterWrite}
                        impactPanel={matching?.impact ?? null}
                      />
                    </li>
                  );
                })}
              </ol>
            ) : null}
            <ActionListFootnote
              autopilotOn={autopilotOn}
              actions={platformActions}
              followupCount={followupCount}
              brandSlug={slug}
            />
            <DismissedDisclosure campaignId={campaignId} />
          </article>
        );
      })()}
    </div>
  );
}

/** Autopilot-on view of mag's actions on this campaign. Replaces the
 *  pending-recommendation list with a history of what mag has
 *  already shipped: title + rationale snapshots frozen at execute
 *  time + "shipped Nh ago" + an inline impact panel per row. Newest
 *  first so the most recent activity is at the top.
 *
 *  Empty state speaks to the autopilot mental model ("i haven't
 *  needed to ship anything yet"). Loading state mirrors the rest
 *  of the page's quiet skeletons. */
function ExecutedActionsHistory({
  executions,
  loading,
}: {
  executions: TakeoverExecutionItem[];
  loading: boolean;
}) {
  const executionCount = executions.length;
  if (loading && executions.length === 0) {
    return (
      <article className="rounded-2xl border-[1.5px] border-[var(--line-strong)] bg-white p-5 shadow-[4px_4px_0_var(--plum)]">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
          {'↘'} <Trans>loading history…</Trans>
        </p>
      </article>
    );
  }
  if (executions.length === 0) {
    return (
      <article className="rounded-2xl border-[1.5px] border-[var(--line-strong)] bg-white p-5 shadow-[4px_4px_0_var(--plum)]">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
          {'↘'} <Trans>what i've done · 0</Trans>
        </p>
        <p className="mt-2 mag-voice text-[14px] leading-[1.45] text-[var(--ink)]/75">
          <Trans>
            “autopilot's on but i haven't needed to ship anything on this campaign yet. i'll surface
            actions here as i take them.”
          </Trans>
        </p>
        {/* Heartbeat indicator rendered even on the empty state —
            without it the founder has no signal that mag is in fact
            scanning the campaign on a cadence, which is precisely
            what they need to know in the "nothing's happened yet"
            case. */}
        <div className="mt-3 flex justify-end border-t border-[var(--line)] pt-3">
          <NextHeartbeatStrip />
        </div>
      </article>
    );
  }
  return (
    <article className="rounded-2xl border-[1.5px] border-[var(--line-strong)] bg-white p-5 shadow-[4px_4px_0_var(--plum)]">
      <div className="flex items-baseline justify-between gap-3">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
          {'↘'} <Trans>what i've done · {executionCount}</Trans>
        </p>
        <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--ink)]/55">
          <Trans>newest first</Trans>
        </span>
      </div>
      <ol className="mt-4 flex flex-col gap-3">
        {executions.map((item) => (
          <li
            key={item.execution.id}
            className="rounded-xl border border-[var(--ink)]/12 bg-[var(--paper)] p-4 sm:p-5"
          >
            <ExecutedActionRow item={item} />
          </li>
        ))}
      </ol>
      <div className="mt-4 flex flex-wrap items-baseline justify-between gap-2 border-t border-[var(--line)] pt-3">
        <p className="text-[12px] leading-[1.5] text-[var(--ink)]/55">
          {'↘'}{' '}
          <Trans>
            autopilot is on — anything i do shows up here. flip the toggle off above to take the
            wheel back.
          </Trans>
        </p>
        <NextHeartbeatStrip />
      </div>
    </article>
  );
}

/** Tiny "next mag check · in N min" countdown sourced from the
 *  autopilot heartbeat endpoint. Polls every 60s so the displayed
 *  countdown stays accurate without being chatty; the api hands
 *  back an authoritative nextTickAt so this component is purely
 *  presentational. Renders nothing when the api returns
 *  nextTickAt=null (server hasn't completed a heartbeat yet —
 *  usually within the first 30 min after a restart). */
function NextHeartbeatStrip() {
  const { t } = useLingui();
  const q = useQuery({
    queryKey: ['autopilot', 'heartbeat'],
    queryFn: fetchAutopilotHeartbeat,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  // Re-render every 30s so the displayed countdown ticks down without
  // re-fetching the api. Cheap — only the strip re-renders.
  const [, force] = useState(0);
  useEffect(() => {
    const t = window.setInterval(() => force((n) => n + 1), 30_000);
    return () => window.clearInterval(t);
  }, []);
  const nextTickAt = q.data?.nextTickAt;
  if (!nextTickAt) {
    return (
      <span className="font-mono text-[10.5px] uppercase tracking-[0.1em] text-[var(--ink)]/45">
        <Trans>next read · warming up</Trans>
      </span>
    );
  }
  const msLeft = new Date(nextTickAt).getTime() - Date.now();
  const minutesLeft = Math.max(0, Math.round(msLeft / 60_000));
  // "in 1 min" / "in 5 min" is Intl's job too — a FUTURE offset, which is
  // what the positive sign means. Only "imminent" is copy.
  const label = minutesLeft === 0 ? t`imminent` : formatRelativeTimeCompact(nextTickAt);
  return (
    <span className="font-mono text-[10.5px] uppercase tracking-[0.1em] text-[var(--ink)]/55">
      {'↘'} <Trans>next mag check · {label}</Trans>
    </span>
  );
}

function ExecutedActionRow({ item }: { item: TakeoverExecutionItem }) {
  const { execution, impact } = item;
  // Named before the message sees them: an expression inside <Trans>
  // extracts as {0}, and a translator reordering an unnamed placeholder
  // is guessing at what it holds.
  const shippedAgo = formatShippedAgo(impact?.shippedAgoMinutes ?? 0);
  const justNow = formatShippedAgo(0);
  const spend = impact?.metrics ? formatNum(impact.metrics.costUsd, 0) : '';
  const windowDays = impact?.metrics?.windowDays ?? 0;
  const clicks = impact?.metrics ? formatNum(impact.metrics.clicks, 0) : '';
  const impressions = impact?.metrics ? formatNum(impact.metrics.impressions, 0) : '';

  return (
    <div className="flex-1">
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-[14px] font-bold tracking-[-0.01em] text-[var(--ink)]">
          {execution.titleSnapshot}
        </p>
        {impact ? (
          <span className="shrink-0 font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--ink)]/55">
            <Trans>shipped {shippedAgo}</Trans>
          </span>
        ) : (
          <span className="shrink-0 font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--ink)]/55">
            <Trans>shipped {justNow}</Trans>
          </span>
        )}
      </div>
      <p className="mt-1 text-[13px] leading-[1.5] text-[var(--ink)]/75">
        {execution.rationaleSnapshot}
      </p>
      {impact ? (
        <div className="mt-2 rounded-lg border border-[var(--ink)]/10 bg-white px-3 py-2">
          <p className="font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--ink)]/55">
            ↘{' '}
            {impact.resourceKind === 'budget' ? (
              <Trans>campaign</Trans>
            ) : (
              impact.resourceKind.replace('_', ' ')
            )}{' '}
            · {impact.resourceLabel}
          </p>
          <p className="mt-1 mag-voice text-[12.5px] leading-[1.4] text-[var(--ink)]">
            “{impact.voiceLine}”
          </p>
          {impact.metrics ? (
            <div className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-1 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/65 sm:grid-cols-4">
              <span>
                <Trans>
                  ${spend} / {windowDays}d
                </Trans>
              </span>
              <span>
                <Trans>{clicks} clicks</Trans>
              </span>
              <span>
                <Trans>{impressions} impr</Trans>
              </span>
              <span>
                {plural(impact.metrics.conversions, {
                  0: '0 conv',
                  other: `${formatNum(impact.metrics.conversions, 1)} conv`,
                })}
              </span>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** Edge-case strip surfaced only when autopilot is ON and mag has
 *  recommendations she can't autopilot (autoApplyEligible: false,
 *  or swap_rsa without a draft). Without this, those actions would
 *  disappear into the void — the history block only shows what's
 *  been shipped, not what's pending. The strip uses the same
 *  ActionItem rendering as the autopilot-off path so the approve /
 *  dismiss controls behave identically. */
function NeedsYourEyeStrip({
  actions,
  campaignId,
  brandId,
  snapshot,
  setSnapshot,
  setMagReadState,
  writesAvailable,
  onOpenSwap,
  onOpenNewRsaWithSeed,
  onOpenNewAdGroupWithSeed,
  onAfterWrite,
  executionsBySignature,
}: {
  actions: TakeoverActionItem[];
  campaignId: string;
  brandId: string;
  snapshot: CampaignSnapshot;
  setSnapshot: React.Dispatch<React.SetStateAction<CampaignSnapshot>>;
  setMagReadState: React.Dispatch<React.SetStateAction<TakeoverRead | null>>;
  writesAvailable: boolean;
  onOpenSwap: (target: SwapTarget) => void;
  onOpenNewRsaWithSeed: (target: NewRsaTarget) => void;
  onOpenNewAdGroupWithSeed: (target: NewAdGroupTarget) => void;
  onAfterWrite: () => Promise<void>;
  executionsBySignature: Map<string, TakeoverExecutionItem>;
}) {
  const needsEyeCount = actions.length;
  return (
    <article className="rounded-2xl border-[1.5px] border-[var(--spell)] bg-white p-5 shadow-[4px_4px_0_var(--spell)]">
      <div className="flex items-baseline justify-between gap-3">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--ink)]">
          {'↘'} <Trans>still needs your eye · {needsEyeCount}</Trans>
        </p>
        <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--ink)]/55">
          <Trans>autopilot can't take these</Trans>
        </span>
      </div>
      <p className="mt-2 mag-voice text-[13px] leading-[1.45] text-[var(--ink)]/75">
        <Trans>
          “these are recommendations i don't think autopilot should ship without you looking.
          approve when you're ready, or dismiss if you disagree.”
        </Trans>
      </p>
      <ol className="mt-4 flex flex-col gap-3">
        {actions.map((a, i) => {
          const sig = a.execution ? takeoverActionSignature(a.execution) : null;
          const matching = sig ? executionsBySignature.get(sig) : undefined;
          return (
            <li
              key={a.title}
              className="flex gap-3 rounded-xl border border-[var(--ink)]/12 bg-[var(--paper)] p-4 transition-colors duration-150 hover:border-[var(--ink)]/22 sm:gap-4 sm:p-5"
            >
              <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full border-[1.5px] border-[var(--ink)] bg-[var(--spell)] font-mono text-[10px] font-bold">
                {i + 1}
              </span>
              <ActionItem
                campaignId={campaignId}
                action={a}
                brandId={brandId}
                snapshot={snapshot}
                setSnapshot={setSnapshot}
                setMagReadState={setMagReadState}
                writesAvailable={writesAvailable}
                onOpenSwap={onOpenSwap}
                onOpenNewRsaWithSeed={onOpenNewRsaWithSeed}
                onOpenNewAdGroupWithSeed={onOpenNewAdGroupWithSeed}
                onAfterWrite={onAfterWrite}
                impactPanel={matching?.impact ?? null}
              />
            </li>
          );
        })}
      </ol>
    </article>
  );
}

/** Founder-facing "dismissed earlier" log under the action list.
 *  Closed by default; opens to show every recommendation the founder
 *  has dismissed for this campaign, with a "↻ bring back" restore
 *  link per entry. Hidden entirely when nothing's dismissed so the
 *  surface stays quiet for the common case. */
function DismissedDisclosure({ campaignId }: { campaignId: string }) {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const q = useQuery({
    queryKey: ['imported-campaign', campaignId, 'dismissals'],
    queryFn: () => listTakeoverDismissals(campaignId),
    staleTime: 30_000,
  });
  const dismissals = q.data ?? [];
  const dismissalCount = dismissals.length;
  if (dismissals.length === 0) return null;
  const handleRestore = async (dismissalId: string) => {
    try {
      await restoreTakeoverDismissal(campaignId, dismissalId);
      magToast.success(t`brought it back.`, t`i'll re-surface it on the next refresh.`);
      await queryClient.invalidateQueries({
        queryKey: ['imported-campaign', campaignId, 'dismissals'],
      });
      await queryClient.invalidateQueries({ queryKey: ['imported-campaign', campaignId] });
    } catch (err) {
      magToast.error("couldn't restore.", founderErrorCopy(err, 'try again in a sec.'));
    }
  };
  return (
    <div className="mt-4 border-t border-[var(--line)] pt-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="font-mono text-[10.5px] uppercase tracking-[0.12em] text-[var(--ink)]/55 transition-colors duration-150 hover:text-[var(--ink)]"
      >
        {'↘'} <Trans>{dismissalCount} dismissed earlier</Trans> {open ? '·' : '→'}
      </button>
      {open ? (
        <ul className="mt-3 flex flex-col gap-2">
          {dismissals.map((d) => {
            const note = d.decisionNote;
            return (
              <li
                key={d.id}
                className="rounded-lg border border-[var(--ink)]/10 bg-[var(--paper)]/60 px-3 py-2"
              >
                <div className="flex items-baseline justify-between gap-2">
                  <p className="text-[12.5px] font-semibold text-[var(--ink)]/75 line-through decoration-[var(--ink)]/30">
                    {d.titleSnapshot}
                  </p>
                  <button
                    type="button"
                    onClick={() => void handleRestore(d.id)}
                    className="shrink-0 font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--plum)] hover:text-[var(--ink)]"
                  >
                    {'↻'} <Trans>bring back</Trans>
                  </button>
                </div>
                <p className="mt-0.5 text-[11.5px] leading-[1.45] text-[var(--ink)]/55">
                  {d.rationaleSnapshot}
                </p>
                {note ? (
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/45">
                    {'↘'} <Trans>your note · {note}</Trans>
                  </p>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

/** Footnote under the action list. Reflects the AIaS model: autopilot
 *  on means mag is the agency, executing by default. The footnote only
 *  flags exceptions: suppressed actions, the swap_rsa hold-out (no
 *  draft copy in schema yet), and text-only items that have been
 *  routed to the followups inbox. */
function ActionListFootnote({
  autopilotOn,
  actions,
  followupCount,
  brandSlug,
}: {
  autopilotOn: boolean;
  /** Already filtered to actions that have an execution block. */
  actions: TakeoverActionItem[];
  /** [12.2L-108] count of text-only actions converted to followups. */
  followupCount: number;
  brandSlug: string;
}) {
  const autoCount = actions.filter(isAutoApplyEligible).length;
  const total = actions.length;
  const reviewCount = total - autoCount;
  let copy: string;
  if (!autopilotOn) {
    copy = "↘ approve any of these and i'll execute on google ads. nothing autonomous.";
  } else if (total === 0) {
    copy = '↘ autopilot on — nothing for me to ship on this campaign right now.';
  } else if (reviewCount === 0) {
    copy =
      "↘ autopilot on — i'm taking everything on the next tick. read what i did in the audit log.";
  } else if (autoCount === 0) {
    copy = '↘ autopilot on — copy swaps still need your eye.';
  } else {
    copy = `↘ autopilot on — i'll ship ${autoCount} on the next tick. ${reviewCount} need your eye (copy swaps + the ones i flagged).`;
  }
  return (
    <div className="mt-4 border-t border-[var(--line)] pt-3">
      <p className="text-[12px] leading-[1.5] text-[var(--ink)]/55">{copy}</p>
      {followupCount > 0 ? (
        <Link
          to="/brand/$slug/home"
          params={{ slug: brandSlug }}
          search={{ triage: 'followups' as const }}
          className="mt-2 inline-block font-mono text-[10.5px] uppercase tracking-[0.12em] text-[var(--plum)] no-underline transition-colors duration-150 hover:text-[var(--ink)]"
        >
          {'↘'}{' '}
          {plural(followupCount, {
            one: '# strategy step in your followups',
            other: '# strategy steps in your followups',
          })}{' '}
          {'→'}
        </Link>
      ) : null}
    </div>
  );
}

/** s207 — one action item. When mag emits an `execution` block AND
 *  writes are available, render an "approve" button that dispatches
 *  the matching Phase B write (pause/enable/budget) or opens the
 *  RSA swap modal pre-filled with the existing copy. Absent
 *  execution → text-only recommendation. */
function ActionItem({
  campaignId,
  action,
  brandId,
  snapshot,
  setSnapshot,
  setMagReadState,
  writesAvailable,
  onOpenSwap,
  onOpenNewRsaWithSeed,
  onOpenNewAdGroupWithSeed,
  onAfterWrite,
  impactPanel,
}: {
  campaignId: string;
  action: TakeoverActionItem;
  brandId: string;
  snapshot: CampaignSnapshot;
  setSnapshot: React.Dispatch<React.SetStateAction<CampaignSnapshot>>;
  setMagReadState: React.Dispatch<React.SetStateAction<TakeoverRead | null>>;
  writesAvailable: boolean;
  onOpenSwap: (target: SwapTarget) => void;
  onOpenNewRsaWithSeed: (target: NewRsaTarget) => void;
  onOpenNewAdGroupWithSeed: (target: NewAdGroupTarget) => void;
  onAfterWrite: () => Promise<void>;
  /** Per-action impact data — populated for ✓ shipped actions whose
   *  approval was captured in `takeover_action_executions`. Null
   *  when the action isn't shipped yet OR when no execution row was
   *  recorded (legacy approvals, swap/create paths that bypass the
   *  actionContext wiring). */
  impactPanel: TakeoverImpactPanel | null;
}) {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const exec = action.execution;
  const alreadyShipped = exec ? isActionApplied(exec, snapshot) : false;
  const canExecute = writesAvailable && exec !== undefined && !alreadyShipped;

  const handleDismiss = async () => {
    if (!exec || dismissing) return;
    setDismissing(true);
    try {
      await dismissTakeoverRecommendation(campaignId, {
        execution: exec,
        title: action.title,
        rationale: action.rationale,
      });
      // Drop the action from the in-memory magRead so the row
      // disappears immediately. Server already filters dismissed
      // signatures on the next GET / refresh.
      setMagReadState((prev) =>
        prev ? { ...prev, actions: prev.actions.filter((a) => a.title !== action.title) } : prev,
      );
      magToast.success(
        "noted — i won't bring this back up.",
        'you can restore it from the "dismissed earlier" list any time.',
      );
      await queryClient.invalidateQueries({
        queryKey: ['imported-campaign', campaignId, 'dismissals'],
      });
    } catch (err) {
      magToast.error("couldn't dismiss.", founderErrorCopy(err, 'try again.'));
    } finally {
      setDismissing(false);
    }
  };

  const handleApprove = async () => {
    if (!exec) return;
    setBusy(true);
    // Archival context attached to every direct-execute write so the
    // api can record a takeover_action_executions row alongside the
    // mutation. Frozen at click time (title/rationale can drift
    // across mag refreshes; the founder approved the version they
    // saw). Swap + create paths handle this inside their modals.
    const actionContext: TakeoverActionContextPayload = {
      importedCampaignId: campaignId,
      title: action.title,
      rationale: action.rationale,
      execution: exec,
    };
    // Set to true on direct-execute paths (pause/enable/budget) so
    // we can fire the background refresh after the try block. Swap +
    // create paths open a modal here and fire refresh from THAT
    // modal's success callback instead — refreshing now would race
    // against stale data.
    let firedDirectWrite = false;
    try {
      if (exec.kind === 'pause_campaign' || exec.kind === 'enable_campaign') {
        const result = await setGoogleCampaignStatus(
          brandId,
          exec.resourceId,
          exec.kind === 'pause_campaign' ? 'PAUSED' : 'ENABLED',
          actionContext,
        );
        toastApproved(exec.kind, result.via);
        setSnapshot((s) =>
          s.providerCampaignId === exec.resourceId
            ? { ...s, status: exec.kind === 'pause_campaign' ? 'paused' : 'enabled' }
            : s,
        );
        firedDirectWrite = true;
      } else if (exec.kind === 'pause_ad_group' || exec.kind === 'enable_ad_group') {
        const result = await setGoogleAdGroupStatus(
          brandId,
          exec.resourceId,
          exec.kind === 'pause_ad_group' ? 'PAUSED' : 'ENABLED',
          actionContext,
        );
        toastApproved(exec.kind, result.via);
        setSnapshot((s) => ({
          ...s,
          adGroups: s.adGroups.map((ag) =>
            ag.providerAdGroupId === exec.resourceId
              ? { ...ag, status: exec.kind === 'pause_ad_group' ? 'paused' : 'enabled' }
              : ag,
          ),
        }));
        firedDirectWrite = true;
      } else if (exec.kind === 'pause_ad' || exec.kind === 'enable_ad') {
        // Resolve the parent ad_group id from the snapshot — Google
        // ad_group_ad resource names are compound and need both ids.
        const targetAd = snapshot.ads.find((a) => a.providerAdId === exec.resourceId);
        const adGroupId = targetAd?.providerAdGroupId ?? snapshot.adGroups[0]?.providerAdGroupId;
        if (!adGroupId) {
          magToast.error(
            "couldn't approve this action.",
            "i couldn't find the ad's parent ad group in the snapshot.",
          );
          return;
        }
        const result = await setGoogleAdStatus(
          brandId,
          exec.resourceId,
          adGroupId,
          exec.kind === 'pause_ad' ? 'PAUSED' : 'ENABLED',
          actionContext,
        );
        toastApproved(exec.kind, result.via);
        setSnapshot((s) => ({
          ...s,
          ads: s.ads.map((ad) =>
            ad.providerAdId === exec.resourceId
              ? { ...ad, status: exec.kind === 'pause_ad' ? 'paused' : 'enabled' }
              : ad,
          ),
        }));
        firedDirectWrite = true;
      } else if (exec.kind === 'adjust_budget') {
        const result = await updateGoogleBudget(
          brandId,
          exec.resourceId,
          exec.newDailyBudgetUsd,
          actionContext,
        );
        const mock = result.via === 'mock' ? ' (mock)' : '';
        magToast.success(
          `budget set to $${exec.newDailyBudgetUsd}/day${mock}.`,
          mock ? 'mock — unset GOOGLE_ADS_USE_MOCK_CLIENT to ship for real.' : '',
        );
        setSnapshot((s) => ({ ...s, dailyBudgetUsd: exec.newDailyBudgetUsd }));
        firedDirectWrite = true;
      } else if (exec.kind === 'swap_rsa') {
        // [12.2L-110] Approval opens the modal. Pre-fill depends on
        // whether mag carried a draft: with drafts → modal shows
        // mag's rewrite; without → modal falls back to existing copy
        // for hand-rewriting. The dispatch happens from inside the
        // modal either way.
        void snapshot;
        const draft =
          exec.draftHeadlines && exec.draftDescriptions && exec.draftFinalUrl
            ? {
                headlines: exec.draftHeadlines,
                descriptions: exec.draftDescriptions,
                finalUrl: exec.draftFinalUrl,
              }
            : null;
        onOpenSwap({ adId: exec.resourceId, draft, actionContext });
      } else if (exec.kind === 'create_ad_group') {
        // s209 — opens the new-ad-group modal pre-filled with mag's
        // draft. Founder reviews + edits before shipping (creates
        // are net-new spend → human-in-the-loop).
        onOpenNewAdGroupWithSeed({
          seed: {
            name: exec.draftName,
            cpcBidUsd: exec.draftCpcBidUsd,
            keywords: exec.draftKeywords,
            ...(exec.draftInitialAd
              ? {
                  initialAd: {
                    headlines: exec.draftInitialAd.headlines,
                    descriptions: exec.draftInitialAd.descriptions,
                    finalUrl: exec.draftInitialAd.finalUrl,
                  },
                }
              : {}),
          },
          actionContext,
        });
      } else if (exec.kind === 'create_rsa') {
        // s209 — opens the new-rsa modal pre-filled with mag's draft.
        onOpenNewRsaWithSeed({
          adGroupResourceId: exec.adGroupResourceId,
          seed: {
            headlines: exec.draftHeadlines,
            descriptions: exec.draftDescriptions,
            finalUrl: exec.draftFinalUrl,
          },
          actionContext,
        });
      }
    } catch (err) {
      magToast.error("couldn't approve.", founderErrorCopy(err));
    } finally {
      setBusy(false);
    }
    if (firedDirectWrite) {
      // Background — don't block the success toast on the
      // server round-trip + agent re-run.
      void onAfterWrite();
    }
  };

  return (
    <div className="flex-1">
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-[14px] font-bold tracking-[-0.01em] text-[var(--ink)]">{action.title}</p>
        <BlastRadiusPill radius={action.blastRadius} />
      </div>
      <p className="mt-1 text-[13px] leading-[1.5] text-[var(--ink)]/75">{action.rationale}</p>
      {alreadyShipped ? (
        <div className="mt-2 flex flex-col items-end gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--sage)]/40 bg-[var(--sage)]/12 px-2.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--sage)]">
            {'✓'} <Trans>shipped</Trans>
          </span>
          {impactPanel ? <ImpactDisclosure impact={impactPanel} /> : null}
        </div>
      ) : canExecute ? (
        <div className="mt-2 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => void handleDismiss()}
            disabled={dismissing || busy}
            className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-[var(--ink)]/50 transition-colors duration-150 hover:text-[var(--ink)] disabled:opacity-50"
          >
            {dismissing ? <Trans>dismissing…</Trans> : <Trans>dismiss</Trans>}
          </button>
          <button
            type="button"
            onClick={() => void handleApprove()}
            disabled={busy || dismissing}
            className="btn btn-ghost text-[11px] disabled:opacity-60"
          >
            {busy ? (
              <Trans>working…</Trans>
            ) : exec?.kind === 'swap_rsa' ? (
              <Trans>review + swap</Trans>
            ) : exec?.kind === 'adjust_budget' ? (
              <Trans>set ${exec.newDailyBudgetUsd}/day</Trans>
            ) : exec?.kind === 'create_ad_group' ? (
              <Trans>review + ship ad group</Trans>
            ) : exec?.kind === 'create_rsa' ? (
              <Trans>review + ship rsa</Trans>
            ) : (
              <Trans>approve · execute</Trans>
            )}
            <span className="btn-arrow">→</span>
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** ↘ how's it doing? — per-action impact disclosure under ✓ shipped.
 *  Closed by default; opens to show shippedAt + per-resource
 *  metrics + a mag-voice line that handles the "too early to tell"
 *  case for fresh launches. Pure presentation — all the logic lives
 *  server-side in `buildImpactPanel`. */
function ImpactDisclosure({ impact }: { impact: TakeoverImpactPanel }) {
  const spend = impact.metrics ? formatNum(impact.metrics.costUsd, 0) : '';
  const windowDays = impact.metrics?.windowDays ?? 0;
  const clicks = impact.metrics ? formatNum(impact.metrics.clicks, 0) : '';
  const impressions = impact.metrics ? formatNum(impact.metrics.impressions, 0) : '';
  const [open, setOpen] = useState(false);
  const ageLabel = formatShippedAgo(impact.shippedAgoMinutes);
  return (
    <div className="w-full">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--plum)] transition-colors duration-150 hover:text-[var(--ink)]"
      >
        {'↘'} <Trans>how's it doing? · shipped {ageLabel}</Trans> {open ? '·' : '→'}
      </button>
      {open ? (
        <div className="mt-2 rounded-lg border border-[var(--ink)]/10 bg-[var(--paper)]/60 px-3 py-2.5">
          <p className="font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--ink)]/55">
            ↘{' '}
            {impact.resourceKind === 'budget' ? (
              <Trans>campaign</Trans>
            ) : (
              impact.resourceKind.replace('_', ' ')
            )}{' '}
            · {impact.resourceLabel}
          </p>
          <p className="mt-1.5 mag-voice text-[13px] leading-[1.45] text-[var(--ink)]">
            “{impact.voiceLine}”
          </p>
          {impact.metrics ? (
            <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 font-mono text-[10.5px] uppercase tracking-[0.08em] text-[var(--ink)]/65 sm:grid-cols-4">
              <span>
                <Trans>
                  ${spend} / {windowDays}d
                </Trans>
              </span>
              <span>
                <Trans>{clicks} clicks</Trans>
              </span>
              <span>
                <Trans>{impressions} impr</Trans>
              </span>
              <span>
                {plural(impact.metrics.conversions, {
                  0: '0 conv',
                  other: `${formatNum(impact.metrics.conversions, 1)} conv`,
                })}
              </span>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** Wave 2's helper. "just now" / "5 min ago" / "3h ago" / "2d ago" were
 *  English assembled in a .ts helper — no JSX to flag and no Intl call to
 *  grep, so nothing here could see them. Intl.RelativeTimeFormat emits
 *  the localized narrow form directly, so none of the four needs a
 *  catalog entry. */
function formatShippedAgo(minutes: number): string {
  return formatRelativeTimeCompact(Date.now() - minutes * 60_000);
}

/** Self-detect "this action's target state is already achieved" by
 *  reading the current snapshot. Used to swap the action's button
 *  for a "✓ shipped" badge once the founder approved the matching
 *  write — the magRead itself doesn't track applied actions, but
 *  the snapshot (which optimistically updates on each success)
 *  knows what's already done.
 *
 *  Returns true only when the desired-state is unambiguously
 *  matched. Conservative on creates (no reliable way to dedupe by
 *  name alone) — those keep their button until the snapshot
 *  refreshes from the server. */
function isActionApplied(exec: TakeoverActionExecution, snapshot: CampaignSnapshot): boolean {
  switch (exec.kind) {
    case 'pause_campaign':
      return snapshot.status === 'paused' || snapshot.status === 'removed';
    case 'enable_campaign':
      return snapshot.status === 'enabled';
    case 'pause_ad_group': {
      const ag = snapshot.adGroups.find((a) => a.providerAdGroupId === exec.resourceId);
      return ag?.status === 'paused' || ag?.status === 'removed';
    }
    case 'enable_ad_group': {
      const ag = snapshot.adGroups.find((a) => a.providerAdGroupId === exec.resourceId);
      return ag?.status === 'enabled';
    }
    case 'pause_ad': {
      const ad = snapshot.ads.find((a) => a.providerAdId === exec.resourceId);
      return ad?.status === 'paused' || ad?.status === 'removed';
    }
    case 'enable_ad': {
      const ad = snapshot.ads.find((a) => a.providerAdId === exec.resourceId);
      return ad?.status === 'enabled';
    }
    case 'adjust_budget':
      // Budget match within $0.50 — the snapshot rounds + Google
      // microsecond conversion drops sub-dollar precision.
      return Math.abs(snapshot.dailyBudgetUsd - exec.newDailyBudgetUsd) < 0.5;
    case 'adjust_ad_group_bid': {
      // Same tolerance logic as adjust_budget: the snapshot's bid is
      // reconstructed from micros, so exact equality is the wrong test.
      // 1c is well inside google's own precision and well outside any
      // raise a founder would make deliberately.
      const ag = snapshot.adGroups.find((a) => a.providerAdGroupId === exec.resourceId);
      const current = ag?.keywords?.[0]?.effectiveCpcBidUsd;
      return current !== undefined && Math.abs(current - exec.newCpcBidUsd) < 0.01;
    }
    case 'pause_keyword': {
      const ag = snapshot.adGroups.find((a) => a.providerAdGroupId === exec.adGroupResourceId);
      // Conservative: only counts as applied when we can SEE the
      // keyword and it is not enabled. A keyword missing from a capped
      // array must not read as "paused" — absence is not evidence.
      const kw = ag?.keywords?.find((k) => k.criterionId === exec.resourceId);
      return kw !== undefined && kw.status !== 'enabled';
    }
    case 'swap_rsa': {
      // The swap flow marks the old ad removed in the local
      // snapshot once the modal succeeds. If the targeted ad is
      // gone, the swap shipped — there's no "swap an already-
      // removed ad" case to confuse this with.
      const ad = snapshot.ads.find((a) => a.providerAdId === exec.resourceId);
      return ad?.status === 'removed' || ad === undefined;
    }
    case 'create_ad_group': {
      // Match by the draft's ad-group name — the dashboard's
      // optimistic onCreated handler adds the new ad group under
      // exec.draftName, so this gate fires the moment the create
      // modal succeeds. A founder who chose a different name in the
      // modal would defeat this, but that's the rare case; the
      // server-side refresh would still drop the action shortly.
      const draftName = exec.draftName.toLowerCase();
      return snapshot.adGroups.some((ag) => ag.name.toLowerCase() === draftName);
    }
    case 'create_rsa': {
      // The new-rsa optimistic update adds an ad named
      // "rsa · just added" inside the target ad group. We match on
      // that marker rather than headlines so a draft that the
      // founder rewrote in the modal still counts as shipped.
      // Brittle if other surfaces ever use the same name string —
      // not a problem today since the marker is dashboard-internal.
      return snapshot.ads.some(
        (ad) =>
          ad.providerAdGroupId === exec.adGroupResourceId &&
          (ad.name === 'rsa · just added' || ad.name === 'rsa · just shipped'),
      );
    }
  }
}

/** Toast helper for the simple execute paths. Swap + budget have
 *  their own copy. */
function toastApproved(
  kind:
    | 'pause_campaign'
    | 'enable_campaign'
    | 'pause_ad_group'
    | 'enable_ad_group'
    | 'pause_ad'
    | 'enable_ad',
  via: 'mock' | 'google',
) {
  const verb = kind.startsWith('pause') ? 'paused' : 'enabled';
  const target = kind.includes('campaign')
    ? 'campaign'
    : kind.includes('ad_group')
      ? 'ad group'
      : 'ad';
  const mock = via === 'mock' ? ' (mock)' : '';
  magToast.success(`${target} ${verb}${mock}.`, '');
}

function BlastRadiusPill({ radius }: { radius: 'low' | 'medium' | 'high' }) {
  const cls =
    radius === 'low'
      ? 'bg-[var(--paper)] text-[var(--ink)]/70 border border-[var(--line)]'
      : radius === 'medium'
        ? 'bg-[var(--spell)] text-[var(--ink)]'
        : 'bg-[var(--pulse)] text-[var(--cream)]';
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-[0.1em] ${cls}`}
    >
      <Trans>{radius} blast</Trans>
    </span>
  );
}

// ─── Formatting helpers ──────────────────────────────────────────────────

/** Same shape as formatShippedAgo, same reason — see the note there. */
function formatRelative(iso: string): string {
  return formatRelativeTimeCompact(iso);
}

// ─── Import-review findings ──────────────────────────────────────

/** Renders the deterministic red-flag findings produced by the
 *  campaign-import reviewer at import time. Brutalist card matching
 *  the mag-read column's chrome. High-severity findings get a pulse
 *  accent (broken / blocking), medium get spell (degrading), low
 *  get plum (worth noting). Hides entirely when no findings — the
 *  parent only renders this when read.findings is non-empty. */
function FindingsSection({ findings }: { findings: ImportFinding[] }) {
  return (
    <article className="rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-5 shadow-[4px_4px_0_var(--pulse)] sm:p-6">
      <div className="flex items-baseline justify-between gap-3">
        <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
          {'↘'} <Trans>what i found</Trans>
        </p>
        <p className="font-mono text-[9.5px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
          {plural(findings.length, { one: '# red flag', other: '# red flags' })}
        </p>
      </div>
      <p className="mt-2 font-mono text-[10.5px] uppercase tracking-[0.1em] text-[var(--ink)]/55">
        {'↘'}{' '}
        <Trans>
          before i touch anything — here's what was already broken when i imported this campaign
        </Trans>
      </p>
      <ul className="mt-4 flex flex-col gap-2.5">
        {findings.map((f, idx) => (
          <li key={`${f.kind}-${f.resourceId ?? idx}`}>
            <FindingRow finding={f} />
          </li>
        ))}
      </ul>
    </article>
  );
}

function FindingRow({ finding }: { finding: ImportFinding }) {
  const sevShadow =
    finding.severity === 'high'
      ? 'shadow-[2px_2px_0_var(--pulse)]'
      : finding.severity === 'medium'
        ? 'shadow-[2px_2px_0_var(--spell)]'
        : 'shadow-[2px_2px_0_var(--plum)]';
  const sevBadgeBg =
    finding.severity === 'high'
      ? 'bg-[var(--pulse)] text-white'
      : finding.severity === 'medium'
        ? 'bg-[var(--spell)] text-[var(--ink)]'
        : 'bg-white text-[var(--ink)]';
  const sevLabel =
    finding.severity === 'high' ? 'blocker' : finding.severity === 'medium' ? 'degrades' : 'fyi';
  return (
    <div
      className={`rounded-xl border-[1.5px] border-[var(--ink)] bg-white px-3.5 py-3 ${sevShadow}`}
    >
      <div className="flex items-baseline justify-between gap-2">
        <p className="min-w-0 truncate font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--ink)]/85">
          ↘ {finding.kind.replace(/_/g, ' ')}
        </p>
        <span
          className={`shrink-0 rounded-md border-[1.5px] border-[var(--ink)] px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-[0.06em] ${sevBadgeBg}`}
        >
          {sevLabel}
        </span>
      </div>
      <p className="mag-voice mt-1.5 text-[13px] leading-[1.45] text-[var(--ink)]/85">
        “{finding.description}”
      </p>
    </div>
  );
}
